import GhostContentAPI from "@tryghost/content-api";
import { Resource } from "@/types/resource";

interface GhostPostResponse {
  id: string;
  title: string;
  html: string;
  excerpt?: string;
  feature_image: string | null;
  url: string;
  published_at: string | null;
  tags?: { name: string }[];
}

function ghostContentApiVersion(): string {
  const fromEnv = import.meta.env.VITE_GHOST_CONTENT_API_VERSION?.trim();
  return fromEnv && fromEnv.length > 0 ? fromEnv : "v5.0";
}

/** Ghost expects the bare site URL (no trailing slash). */
function normalizeGhostSiteUrl(raw: string): string {
  return raw.trim().replace(/\/+$/, "");
}

function coerceBrowsePosts(response: unknown): GhostPostResponse[] {
  if (Array.isArray(response)) return response as GhostPostResponse[];
  if (response && typeof response === "object" && "posts" in response) {
    const posts = (response as { posts?: unknown }).posts;
    if (Array.isArray(posts)) return posts as GhostPostResponse[];
  }
  return [];
}

function toAbsoluteGhostUrl(siteUrlRaw: string, postUrlRaw: string): string {
  if (/^https?:\/\//i.test(postUrlRaw)) return postUrlRaw;
  const site = siteUrlRaw.replace(/\/+$/, "");
  const path = postUrlRaw.startsWith("/") ? postUrlRaw : `/${postUrlRaw}`;
  return `${site}${path}`;
}

function stripHtmlText(html: string, maxChars: number): string {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const text = (tempDiv.textContent || tempDiv.innerText || "").replace(/\s+/g, " ").trim();
  tempDiv.remove();
  if (text.length <= maxChars) return text;
  return `${text.slice(0, Math.max(0, maxChars - 1)).trim()}…`;
}

function parseTypeSlugFromGhostTags(tags?: { name: string }[]): string | null {
  if (!tags || !tags.length) return null;
  const tagged = tags.find((t) => t.name.includes("#type#"))?.name;
  const raw = tagged?.split("#")[2]?.trim()?.toLowerCase();
  return raw || null;
}

function parseCategoryFromGhostTags(tags?: { name: string }[]): string | undefined {
  const tag = tags?.find((t) => t.name.includes("#category#"));
  if (!tag) return undefined;
  return tag.name.split("#category#")[1]?.trim();
}

/** First media/link URL inside post HTML — used when KG CTA is absent but the body still carries the asset. */
function stripHtmlFirstAssetUrl(html: string): string {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const iframe = tempDiv.querySelector("iframe[src]") as HTMLIFrameElement | null;
  const audio = tempDiv.querySelector("audio[src]") as HTMLAudioElement | null;
  const source = audio?.querySelector("source[src]") as HTMLSourceElement | null;
  const video = tempDiv.querySelector("video[src]") as HTMLVideoElement | null;
  const anchorPdf = Array.from(tempDiv.querySelectorAll("a[href]")).find((a) =>
    /\.pdf($|\?)/i.test((a as HTMLAnchorElement).href)
  ) as HTMLAnchorElement | undefined;

  const url =
    iframe?.src ||
    video?.currentSrc ||
    video?.src ||
    source?.src ||
    audio?.src ||
    anchorPdf?.href ||
    "";

  tempDiv.remove();
  return url;
}

class ResourcesService {
  private api: GhostContentAPI | null = null;

  /** Content API key (same as `VITE_GHOST_PUBLIC_API_KEY`); used when REST fallback runs or SDK is unavailable. */
  private ghostContentApiKey: string | null = null;

  /** Cached Ghost posts (plus user-generated wrappers merged at page level); cleared via `clearCache()`. */
  resources: Resource[] | null = null;

  ghostSiteUrl: string;

  constructor() {
    const url = normalizeGhostSiteUrl(import.meta.env.VITE_GHOST_URL?.trim() ?? "");
    const key = import.meta.env.VITE_GHOST_PUBLIC_API_KEY?.trim() ?? "";

    this.ghostSiteUrl = url;
    this.ghostContentApiKey = key.length > 0 ? key : null;

    if (!url || !key) {
      this.api = null;
      if (import.meta.env.DEV) {
        if (!url && !key) {
          console.info(
            "[resourcesService] Ghost CMS is not configured — add VITE_GHOST_URL and VITE_GHOST_PUBLIC_API_KEY to `.env` at the project root (see `.env.example`). Restart Vite after saving. Get the key from Ghost Admin → Settings → Integrations → Content API."
          );
        } else if (Boolean(url) !== Boolean(key)) {
          console.warn(
            "[resourcesService] Ghost is partially configured — set both VITE_GHOST_URL (e.g. https://cms.heybodhi.ai) and VITE_GHOST_PUBLIC_API_KEY (Content API key: Ghost Admin → Settings → Integrations). Restart the dev server after saving .env."
          );
        }
      }
      return;
    }

    try {
      this.api = new GhostContentAPI({
        url,
        key,
        version: ghostContentApiVersion(),
      });
    } catch (error) {
      console.error("Error initializing GhostContentAPI:", error);
      this.api = null;
    }
  }

  /**
   * Direct Content API fetch — bypasses @tryghost/content-api when the SDK fails (version mismatch,
   * bundler quirks) but env credentials are valid.
   */
  private async fetchPostsViaRest(siteUrl: string, apiKey: string): Promise<GhostPostResponse[]> {
    const all: GhostPostResponse[] = [];
    let page = 1;
    const limit = 50;

    for (;;) {
      const params = new URLSearchParams({
        key: apiKey,
        include: "tags",
        formats: "html",
        limit: String(limit),
        page: String(page),
      });

      const res = await fetch(`${siteUrl}/ghost/api/content/posts/?${params.toString()}`, {
        method: "GET",
        credentials: "omit",
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Ghost Content API HTTP ${res.status}: ${body.slice(0, 400)}`);
      }

      const json = (await res.json()) as {
        posts?: GhostPostResponse[];
        meta?: { pagination?: { pages?: number; next?: number | null } };
      };

      const batch = json.posts ?? [];
      all.push(...batch);

      const totalPages = json.meta?.pagination?.pages ?? 1;
      if (page >= totalPages || batch.length === 0) break;
      page += 1;
      if (page > 100) break;
    }

    return all;
  }

  fetchPosts = async (): Promise<void> => {
    const url = normalizeGhostSiteUrl(this.ghostSiteUrl);
    const key = this.ghostContentApiKey ?? "";

    if (!url || !key) {
      this.resources = [];
      return;
    }

    try {
      const versions = Array.from(new Set([ghostContentApiVersion(), "v5.0", "v6.0", "v4.0"]));

      let posts: GhostPostResponse[] = [];
      let gotResponse = false;
      let lastError: unknown;

      if (this.api) {
        for (const version of versions) {
          try {
            const client = new GhostContentAPI({
              url,
              key,
              version,
            });
            const browseResult = await client.posts.browse({
              limit: "all",
              include: ["tags"],
              formats: ["html"],
            });
            posts = coerceBrowsePosts(browseResult);
            gotResponse = true;
            break;
          } catch (err) {
            lastError = err;
          }
        }
      }

      if (!gotResponse) {
        try {
          posts = await this.fetchPostsViaRest(url, key);
          gotResponse = true;
          if (import.meta.env.DEV) {
            console.info("[resourcesService] Loaded Ghost posts via Content API REST fallback.");
          }
        } catch (err) {
          lastError = err;
        }
      }

      if (!gotResponse) {
        console.error("[resourcesService] Ghost posts fetch failed (SDK and REST).", lastError);
        this.resources = [];
        return;
      }

      const mapped = posts.map((post) => this.parsePost(post)).filter((resource): resource is Resource => Boolean(resource));

      this.resources = mapped.length > 0 ? mapped : [];

      if (mapped.length === 0 && posts.length > 0) {
        console.warn(
          "[resourcesService] Ghost returned posts but none matched the parsers. Posts may lack #type#/CTA/HTML links — check Ghost content format."
        );
      }
    } catch (error) {
      console.error("[resourcesService] Ghost fetch failed unexpectedly.", error);
      this.resources = [];
    }
  };

  getResources = async (): Promise<Resource[]> => {
    if (!this.ghostSiteUrl || !this.ghostContentApiKey) return [];

    if (this.resources) return this.resources;

    await this.fetchPosts();
    return this.resources ?? [];
  };

  clearCache(): void {
    this.resources = null;
  }

  /** Legacy KG CTA cards used by Hey Bodhi Ghost theme. */
  private parseKgCtaResource(post: GhostPostResponse): Resource | null {
    const tags = post.tags ?? [];
    const slug = parseTypeSlugFromGhostTags(tags);
    if (!slug || !["pdf", "audio", "video", "image"].includes(slug)) {
      return null;
    }
    const type = slug as "pdf" | "audio" | "video" | "image";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = post.html ?? "";

    const ctaRoot = tempDiv.querySelector(".kg-cta-card");
    if (!ctaRoot) {
      tempDiv.remove();
      return null;
    }

    const ctaDescription = ctaRoot.querySelector(".kg-cta-text");
    const ctaResource = ctaRoot.querySelector(".kg-cta-button[href]");

    if (
      !(ctaResource instanceof HTMLAnchorElement) ||
      !(ctaDescription instanceof HTMLElement)
    ) {
      tempDiv.remove();
      return null;
    }

    const url = ctaResource.href;
    const metaSnippet = (ctaResource.textContent || ctaResource.innerText || "").split(":")[1]?.trim()
      ?? (ctaResource.textContent || ctaResource.innerText || "").trim()
      ?? "Tap to view";

    const description = (ctaDescription.textContent || ctaDescription.innerText || "").trim();
    tempDiv.remove();

    if (!description) return null;

    const category =
      parseCategoryFromGhostTags(tags) ?? "Library";

    return {
      id: post.id,
      title: post.title,
      description,
      photo: post.feature_image || "",
      meta: metaSnippet,
      type,
      url,
      addedDate: post.published_at ?? new Date().toISOString(),
      category,
    };
  }

  /** When CTA markup is absent, derive a resource from excerpts + canonical URL + inferred media. */
  private parseMinimalResource(post: GhostPostResponse): Resource | null {
    const tags = post.tags ?? [];
    const slug = parseTypeSlugFromGhostTags(tags);

    let type: Resource["type"] =
      slug && ["pdf", "audio", "video", "image", "website"].includes(slug) ? (slug as Resource["type"]) : "website";

    let url = stripHtmlFirstAssetUrl(post.html || "");

    const wantsMedia = type === "pdf" || type === "audio" || type === "video" || type === "image";
    if (wantsMedia && (!url || url === "#")) {
      type = "website";
    }

    if (type === "website" || !url) {
      url = toAbsoluteGhostUrl(this.ghostSiteUrl, post.url);
    }

    let description = post.excerpt?.trim();
    if (description) description = stripHtmlText(description, 400);
    if (!description?.trim()) description = stripHtmlText(post.html || "", 380);
    if (!description?.trim()) {
      description = post.title.trim();
    }
    if (!description) return null;

    const meta =
      type === "pdf" ? "Read"
        : type === "audio" ? "Listen"
        : type === "video" ? "Watch"
        : type === "image" ? "View"
        : "Read";

    const category =
      parseCategoryFromGhostTags(tags) ?? "Library";

    return {
      id: post.id,
      title: post.title,
      description,
      photo: post.feature_image || "",
      meta,
      type,
      url,
      addedDate: post.published_at ?? new Date().toISOString(),
      category,
    };
  };

  private parsePost = (post: GhostPostResponse): Resource | null =>
    this.parseKgCtaResource(post) ?? this.parseMinimalResource(post);

  getFavorites = (userId: string): string[] | null => {
    const favoritesRecord = localStorage.getItem("user_favorites");
    if (!favoritesRecord) return null;

    try {
      const allFavorites = JSON.parse(favoritesRecord) as Record<string, string[]>;
      return allFavorites[userId] || null;
    } catch (error) {
      console.error("Error getting favorites:", error);
      return null;
    }
  };

  storeFavorites = (userId: string, ids: string[]) => {
    let newFavorites: Record<string, string[]> = {};
    const favoritesRecord = localStorage.getItem("user_favorites");

    try {
      if (!favoritesRecord) {
        newFavorites[userId] = ids;
      } else {
        const allFavorites = JSON.parse(favoritesRecord) as Record<string, string[]>;
        newFavorites = { ...allFavorites, [userId]: ids };
      }

      localStorage.setItem("user_favorites", JSON.stringify(newFavorites));
    } catch (error) {
      console.error("Error storing favorite:", error);
    }
  };

  deleteFavorite = (userId: string, id: string) => {
    const userFavorites = this.getFavorites(userId);
    if (!userFavorites) return;

    const newUserFavorites = userFavorites.filter((favorite) => favorite !== id);
    this.storeFavorites(userId, newUserFavorites);
  };

  toggleUserFavorite = (userId: string, id: string) => {
    const userFavorites = this.getFavorites(userId);
    if (!userFavorites) {
      this.storeFavorites(userId, [id]);
      return;
    }

    if (userFavorites.includes(id)) {
      this.deleteFavorite(userId, id);
      return;
    }

    this.storeFavorites(userId, [...userFavorites, id]);
  };
}

export const resourcesService = new ResourcesService();
